import { CCB, FEB, FINWISE, SET_ERROR } from 'helpers/Constants';
import { NAVY, WHITE_BACKGROUND, WHITE_TEXT } from 'styles/Colors';
import { useDispatch, useSelector } from 'react-redux';
import BankVerificationModal from 'reg_redesign/modals/BankVerificationModal';
import CreditReportModal from 'reg_redesign/modals/CreditReportModal';
import ElecCommModal from 'components/rebrand/elecCommDisclosure/ElecCommModal';
import Fieldset from 'components/rebrand/Fieldset';
import LinkButton from '@opploans/link-button-component/LinkButton';
import MarketingDisclosure from 'components/rebrand/MarketingDisclosure';
import React, { useState } from 'react';
import TurnupDisclosures from 'registration/TurnupDisclosures';

const termsOfServiceStyle = {
  backgroundColor: WHITE_BACKGROUND,
  padding: '1rem',
  'h4, p': {
    paddingBottom: '0.5rem'
  },
  li: {
    paddingBottom: '0.75rem'
  },
  'ul, ol': {
    padding: '0.5rem 1.5rem'
  }
};

const footerStyle = {
  backgroundColor: NAVY,
  borderBottomLeftRadius: '3px',
  borderBottomRightRadius: '3px',
  color: WHITE_TEXT,
  marginBottom: '2rem',
  padding: '.75rem 1.25rem',
  textAlign: 'center'
};

export default function ConsentAndDisclosures() {
  const state = useSelector((store) => store.state);
  const productOwner = useSelector((store) => store.productOwner);
  const elecCommDisclosure = useSelector((store) => store.elecCommDisclosure);
  const elecCommDisclosureErrors = useSelector(
    (store) => store.errors.elecCommDisclosure
  );
  const [creditReportOpen, setCreditReportOpen] = useState(false);
  const [bankVerificationOpen, setBankVerificationOpen] = useState(false);
  const dispatch = useDispatch();
  const isCalifornia = state === 'CA';
  const grossMonthlyIncome = useSelector((store) => store.grossMonthlyIncome);
  const shouldShowTurnupDisclosures = grossMonthlyIncome >= 3333; // $40k annually

  let provider = '';
  let privacyPolicy = '';
  switch (productOwner) {
    case FINWISE:
      provider = 'FinWise Bank';
      privacyPolicy =
        'https://privacy.opploans.com/policies?name=finwise-bank#finwise-bank-privacy-policy';
      break;
    case FEB:
      provider = 'First Electronic Bank';
      privacyPolicy =
        'https://privacy.opploans.com/policies?name=first-electronic-bank#first-electronic-bank-privacy-policy';
      break;
    case CCB:
      provider = 'CCBank';
      privacyPolicy =
        'https://privacy.opploans.com/policies?name=capital-community-bank#community-capital-bank-privacy-policy';
      break;
    default:
      break;
  }
  const isBankProduct = Boolean(provider);

  const renderDisclosure = () => {
    return (
      <div css={termsOfServiceStyle}>
        <h3>
          By clicking &quot;Agree and Apply Now&quot; button below, you agree to
          the following:
        </h3>

        <div>
          <ul>
            <li>
              I certify that the information I have supplied in this application
              is true and correct.
            </li>

            <li>
              I agree to the OppLoans&apos;&nbsp;
              <a
                href='https://www.opploans.com/licenses/terms-of-use/'
                target='_blank'
                rel='noreferrer'
              >
                Terms of Use
              </a>
              &nbsp;
              {!isCalifornia && (
                <span>
                  &#32;and OppLoans&apos;&nbsp;
                  <a
                    href='https://privacy.opploans.com/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Privacy Policy
                  </a>
                </span>
              )}
              {isCalifornia && (
                <span>
                  , OppLoans&apos;&nbsp;
                  <a
                    href='https://privacy.opploans.com/'
                    target='_blank'
                    rel='noreferrer'
                  >
                    California Disclosures and Privacy Policy
                  </a>
                </span>
              )}
              {isBankProduct && (
                <span>
                  , and&nbsp;
                  <a href={privacyPolicy} target='_blank' rel='noreferrer'>
                    {provider}
                    &apos;s Privacy Policy
                  </a>
                </span>
              )}
              .&nbsp;
              {!isBankProduct && (
                <span>
                  I authorize and direct OppLoans to share personal information
                  connected to my previous applications and/or financial
                  products that were originated and/or serviced by OppLoans with
                  OppLoans&apos; bank partners for the purpose of originating,
                  facilitating, managing, and/or servicing other financial
                  products that are the subject of this application.
                </span>
              )}
              {isBankProduct && (
                <span>
                  Further, I authorize and direct OppLoans to share personal
                  information, within 30 days of this application, connected to
                  my previous applications and/or financial products that were
                  originated and/or serviced by OppLoans with OppLoans&apos;
                  bank partners for the purpose of originating, and/or
                  facilitating the financial product that is the subject of this
                  application.
                </span>
              )}
            </li>

            <li>
              I understand that OppLoans&nbsp;
              {isBankProduct && (
                <span>
                  and&nbsp;
                  {provider}
                  &nbsp;
                </span>
              )}
              will obtain a copy of my credit report and related information
              from one or more consumer reporting agencies. I consent to the
              Credit Report Pull Authorization, as specified&nbsp;
              <LinkButton
                lightBackground
                onClick={() => setCreditReportOpen(true)}
                id='credit-report-authorization'
              >
                <span css={{ fontSize: '16px' }}>here</span>
              </LinkButton>
            </li>

            <li>
              I expressly authorize OppLoans
              {!isBankProduct && (
                <span>&nbsp;and its affiliates and non-affiliates</span>
              )}
              {isBankProduct && (
                <span>
                  , its affiliates and non-affiliates, and&nbsp;
                  {provider}
                </span>
              )}
              &nbsp;to share among them any transaction history related to my
              financial products or services received through OppLoans or
              serviced by OppLoans for the purpose of evaluating me for credit.
            </li>

            {state === 'CA' && (
              <li className='california-disclaimer'>
                California Residents, a married applicant may apply for a
                separate account, California Finance Lender License No. 603 K
                647
              </li>
            )}

            <li>
              I agree to the&nbsp;
              <LinkButton
                lightBackground
                onClick={() => setBankVerificationOpen(true)}
                additionalStyle={{ textAlign: 'left' }}
              >
                <span css={{ fontSize: '16px' }}>
                  Bank Verification Terms of Service.
                </span>
              </LinkButton>{' '}
              Furthermore, by entering and submitting my banking information in
              the fields provided on the Bank Verification page, I authorize
              OppLoans to access secure bank verification (&quot;BV&quot;)
              reports of my banking data for this application and future
              applications.
            </li>
          </ul>
        </div>
        {state === 'DE' && (
          <div className='delaware-disclaimer'>
            <h4>Disclosure for Schedule of Charges for Closed End Credit</h4>

            <p>
              I acknowledge receipt of the required &quot;Schedule of Charges
              for Closed End Credit&quot; under Title 5 of Delaware
              Administrative Code.
            </p>

            <p>
              A licensee may collect the following charges for closed end credit
              as allowed by 5 Del.C. Ch. 22, Subch. III:
            </p>

            <ol type='I'>
              <li>
                Interest, including interest at variable rates up to 160% paid
                weekly, bi-weekly, semi-monthly, or monthly, based upon the
                schedule of the borrower&apos;s income;
              </li>
              <li>
                fees for services rendered in good faith by the licensee or its
                agents in connection with the loan;
              </li>
              <li>
                reimbursement for expenses incurred in good faith by the
                licensee or its agents in connection with the loan;
              </li>
              <li> late or delinquency charges; </li>
              <li> charges for deferred installment payments; </li>
              <li>
                attorney fees incurred by the licensee as a result of a default
                by the borrower;
              </li>
              <li>
                court, alternative dispute resolution, or other collection costs
                (including collection agency fees) incurred by the licensee as a
                result of a default by the borrower;
              </li>
              <li> deferral charges; and </li>
              <li>
                Returned Payment Fee: In the event loan payments are returned by
                the customer&apos;s financial institution, a $20 NSF fee may be
                charged, as specified in the contract. A customer may only be
                assessed one charge per item.
              </li>
            </ol>
          </div>
        )}
        {state === 'OH' && (
          <div className='ohio-disclaimer'>
            <p>
              The Ohio laws against discrimination require that all creditors
              make credit equally available to all credit worthy customers, and
              that credit reporting agencies maintain separate credit histories
              on each individual upon request. The Ohio Civil Rights Commission
              administers compliance with this law.
            </p>
          </div>
        )}
        {state === 'WI' && (
          <div className='wisconsin-disclaimer'>
            <h4> Notice to Married Applicants</h4>

            <p>
              No provision of a marital property agreement, a unilateral
              statement under &sect;&nbsp;766.59 or a court decree under
              &sect;&nbsp;766.70 adversely affects the interest of the creditor
              unless the creditor, prior to the time credit is granted, is
              furnished a copy of the agreement, statement or decree or has
              actual knowledge of the adverse provision when the obligation to
              the creditor is incurred.
            </p>
          </div>
        )}
        <CreditReportModal
          open={creditReportOpen}
          handleClose={() => setCreditReportOpen(false)}
          provider={provider}
        />
        <BankVerificationModal
          open={bankVerificationOpen}
          handleClose={() => setBankVerificationOpen(false)}
        />
      </div>
    );
  };
  const renderConsentAndDisclosure = () => {
    if (elecCommDisclosureErrors === undefined) {
      dispatch({
        type: SET_ERROR,
        payload: {
          elecCommDisclosure: true
        }
      });
    }

    return (
      <>
        <Fieldset
          label='Consent and Disclosures'
          style={{ padding: '2rem 0', marginBottom: '0rem !important' }}
        >
          <MarketingDisclosure style={{ padding: '0 1.25rem' }} />
          <ElecCommModal />
        </Fieldset>
        {elecCommDisclosure ? (
          <h3 css={footerStyle}>
            Electronic Communications Agreement Consented
          </h3>
        ) : (
          <h3 css={footerStyle}>You must read and sign to continue</h3>
        )}

        {shouldShowTurnupDisclosures && <TurnupDisclosures />}

        {renderDisclosure()}
      </>
    );
  };
  return renderConsentAndDisclosure();
}
